import "./product-details.scss";

import classNames from "classnames";
import JsBarcode from "jsbarcode";
import { observer } from "mobx-react-lite";
import { FunctionComponent, MouseEvent, useEffect, useRef } from "react";

import { ReactComponent as CloseIcon } from "@client/components/reusable/SvgIcons/library/cancel_order_icon.svg";
import { ReactComponent as UndoIcon } from "@client/components/reusable/SvgIcons/library/undo_icon.svg";
import SvgIcon from "@client/components/reusable/SvgIcons/SvgIcon/SvgIcon";
import { useRootContext } from "@client/context/RootContext";
import { Order, Product } from "@client/stores";

interface ProductDetailsProps {
	product: Product;
	order: Order;
	idx: number;
	rejectedProducts: string[];
	addRejectedProduct: (id: string) => void;
	removeRejectedProduct: (id: string) => void;
}

export const ProductDetails: FunctionComponent<ProductDetailsProps> = observer(
	({
		product,
		order,
		idx,
		rejectedProducts,
		addRejectedProduct,
		removeRejectedProduct,
	}) => {
		const { modalStore, vendorStore } = useRootContext();

		const imgRef = useRef<HTMLImageElement>(null);

		const isPickingStatus = order.status === "Picking";
		const showBackToOrderButton = isPickingStatus;

		useEffect(() => {
			if (product.order_details?.barcode) {
				JsBarcode(`#${product.sku}`, product.order_details?.barcode || "", {
					format: "CODE128",
					fontSize: 30,
					textMargin: -4,
				});
			}
		}, [imgRef.current, modalStore.modals.length]);

		function show_barcodeModal(e: MouseEvent) {
			e.preventDefault();
			e.stopPropagation();

			modalStore.addModal({
				id: "barcode-modal-" + product.sku,
				showCloseBtn: true,
				content: (
					<div className="barcode-modal-wrapper">
						<img id={product.sku} className="barcode-img" />
					</div>
				),
			});
		}

		function show_imageGalleryModal(e: MouseEvent) {
			e.preventDefault();
			e.stopPropagation();

			modalStore.addModal({
				id: "gallery-modal-" + product.sku,
				showCloseBtn: true,
				content: <img className="large-img" src={product.tx_details.image} />,
			});
		}

		const isRejected = rejectedProducts.find(
			(rejectedProductId) =>
				rejectedProductId === product.id + "-" + String(idx)
		);

		return (
			<div key={product.sku} className="product-details">
				{isRejected && <div className="product-blur" />}
				{isRejected && showBackToOrderButton && (
					<button
						className="back-to-order"
						onClick={() =>
							removeRejectedProduct(product.id + "-" + String(idx))
						}
					>
						<SvgIcon component={UndoIcon} /> Bring back to order
					</button>
				)}
				<img
					className="product-image"
					src={product.tx_details?.image}
					onClick={show_imageGalleryModal}
				/>
				<div className="product-attributes">
					<div className="product-attribute-title">
						{product.tx_details?.name}
					</div>
					<div className="first-row">
						<div className="product-attribute">
							Size: <span className="bold">{product.order_details?.size}</span>
						</div>
						<div className="product-attribute">
							Color:{" "}
							<span className="bold">{product.order_details?.color}</span>
						</div>
					</div>
					<div className="product-attribute">
						SKU: <span className="bold">{product.sku}</span>
					</div>

					{product.tx_details?.brand &&
						product.tx_details?.brand !== "No vendor" && (
							<div className="product-attribute">
								Brand: <span className="bold">{product.tx_details.brand}</span>
							</div>
						)}
					{vendorStore.vendorDetails?.show_picking_area == "yes" &&
						product.order_details?.pickingarea && (
							<div className="product-attribute">
								Picking area:{" "}
								<span className="bold">
									{product.order_details?.pickingarea}
								</span>
							</div>
						)}
					<div className="barcode-img-container">
						<img
							id={product.sku}
							ref={imgRef}
							className="barcode-img"
							onClick={show_barcodeModal}
						/>
						{!showBackToOrderButton && !isRejected ? null : (
							<button
								className={classNames("reject-product-btn", {
									["rejected"]: isRejected,
								})}
								onClick={() =>
									!isRejected
										? addRejectedProduct(product.id + "-" + String(idx))
										: {}
								}
							>
								{!isRejected && <SvgIcon component={CloseIcon} />}
								{isRejected ? "Rejected" : "Reject"}
							</button>
						)}
					</div>
				</div>
			</div>
		);
	}
);
